import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Group,
  Paper,
  ScrollArea,
  Select,
  Skeleton,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconChevronLeft, IconChevronRight, IconMail, IconPlus } from '@tabler/icons-react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState, type MouseEvent } from 'react';
import { useSearchParams } from 'react-router';
import { sporocilo } from '../api/napaka';
import { api, podatki } from '../api/odjemalec';
import { usePravica } from '../seja/pravice';
import { GlavaZaslona, ZaslonSeznama } from '../seznam/Seznam';
import { barve } from '../tema';
import {
  dneviTedna,
  imeDne,
  isoDan,
  obdobjeTedna,
  PRVA_URA,
  razporedi,
  ura,
  urVDnevu,
  zacetekTedna,
  zamakni,
  ZADNJA_URA,
  type Termin,
} from './termini';
import { TerminOkno, type NovTermin } from './TerminOkno';

/** Višina ene ure v mreži (px). */
const URA_PX = 56;

/**
 * Koledar terminov delavnice (NACRT_DELAVNICA.md, faza 3): teden od ponedeljka do sobote (nedelja, če je v njej
 * termin), na telefonu seznam po dnevih. Klik na prosto mesto odpre nov termin ob tej uri, klik na termin ga odpre
 * (sprememba, potrditev, odpoved, sprejem vozila). Teden ({@code ?teden=YYYY-MM-DD}, ponedeljek) in mehanik
 * ({@code ?mehanik=}) sta v URL-ju.
 */
export function KoledarTerminov() {
  const [parametri, setParametri] = useSearchParams();
  const danes = isoDan(new Date());
  const teden = zacetekTedna(parametri.get('teden') ?? danes);
  const mehanik = parametri.get('mehanik');
  const ureja = usePravica('DELAVNICA', 'UREJANJE');
  const [odprt, setOdprt] = useState<Termin | null>(null);
  const [nov, setNov] = useState<NovTermin | null>(null);

  const nastavi = (spremembe: Record<string, string | null>) => {
    const p = new URLSearchParams(window.location.search);
    Object.entries(spremembe).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    setParametri(p, { replace: true });
  };

  const moznosti = useQuery({
    queryKey: ['delavnica-moznosti'],
    queryFn: ({ signal }) => podatki(api.GET('/api/delavnica/nalogi/moznosti', { signal })),
  });
  const termini = useQuery({
    queryKey: ['termini', teden, mehanik],
    queryFn: ({ signal }) =>
      podatki(
        api.GET('/api/delavnica/termini', {
          signal,
          params: { query: { od: teden, do: zamakni(teden, 6), mehanik: mehanik ? Number(mehanik) : undefined } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const vsi = termini.data ?? [];
  const nedelja = zamakni(teden, 6);
  const dnevi = dneviTedna(teden, vsi.some((t) => t.zacetek?.startsWith(nedelja)) ? 7 : 6);
  const poDnevih = new Map(dnevi.map((d) => [d, vsi.filter((t) => t.zacetek?.startsWith(d))]));
  const mehaniki = moznosti.data?.mehaniki ?? [];
  const novNa = (dan: string, cas = '08:00') => ureja && setNov({ dan, cas });

  return (
    <ZaslonSeznama>
      <GlavaZaslona
        naslov="Termini"
        opis="Koledar naročenih strank. Ob prihodu iz termina sprejmete vozilo."
        pomoc="termini"
        dejanja={
          ureja && (
            <Button
              leftSection={<IconPlus size={18} />}
              onClick={() => novNa(danes >= teden && danes <= nedelja ? danes : teden)}
            >
              Nov termin
            </Button>
          )
        }
      />
      <Group justify="space-between" mb="md" gap="sm">
        <Group gap="xs" wrap="nowrap">
          <ActionIcon
            variant="default"
            size={42}
            aria-label="Prejšnji teden"
            onClick={() => nastavi({ teden: zamakni(teden, -7) })}
          >
            <IconChevronLeft size={20} />
          </ActionIcon>
          <Button variant="default" onClick={() => nastavi({ teden: null })}>
            Danes
          </Button>
          <ActionIcon
            variant="default"
            size={42}
            aria-label="Naslednji teden"
            onClick={() => nastavi({ teden: zamakni(teden, 7) })}
          >
            <IconChevronRight size={20} />
          </ActionIcon>
          <Text fw={700} fz="lg" ml="xs" className="brez-preloma">
            {obdobjeTedna(teden, dnevi.length)}
          </Text>
        </Group>
        <Select
          placeholder="Vsi mehaniki"
          aria-label="Mehanik"
          data={mehaniki.map((m) => ({ value: String(m.id), label: m.ime ?? '' }))}
          value={mehanik}
          onChange={(v) => nastavi({ mehanik: v })}
          clearable
          w={{ base: '100%', xs: 220 }}
        />
      </Group>
      {termini.isError && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={18} />} mb="md" role="alert">
          Terminov ni bilo mogoče naložiti: {sporocilo(termini.error)}
        </Alert>
      )}
      {termini.isPending ? (
        <Skeleton h={480} radius="lg" />
      ) : (
        <>
          <Box visibleFrom="sm">
            <Mreza dnevi={dnevi} danes={danes} poDnevih={poDnevih} odpri={setOdprt} novNa={novNa} />
          </Box>
          <Box hiddenFrom="sm">
            <Seznam dnevi={dnevi} danes={danes} poDnevih={poDnevih} odpri={setOdprt} novNa={novNa} />
          </Box>
        </>
      )}
      <TerminOkno
        termin={odprt}
        nov={nov}
        mehaniki={mehaniki}
        zapri={() => {
          setOdprt(null);
          setNov(null);
        }}
      />
    </ZaslonSeznama>
  );
}

type Pogled = {
  dnevi: string[];
  danes: string;
  poDnevih: Map<string, Termin[]>;
  odpri: (t: Termin) => void;
  novNa: (dan: string, cas?: string) => void;
};

/** Tedenska mreža (namizje): ure levo, dnevi v stolpcih, termini kot bloki po času in trajanju. */
function Mreza({ dnevi, danes, poDnevih, odpri, novNa }: Pogled) {
  const ure = Array.from({ length: ZADNJA_URA - PRVA_URA }, (_, i) => PRVA_URA + i);
  const visina = ure.length * URA_PX;
  const klik = (dan: string, e: MouseEvent<HTMLDivElement>) => {
    const y = e.clientY - e.currentTarget.getBoundingClientRect().top;
    const pol = Math.max(0, Math.floor((y / URA_PX) * 2));
    const h = PRVA_URA + Math.floor(pol / 2);
    novNa(dan, `${String(Math.min(h, 20)).padStart(2, '0')}:${pol % 2 ? '30' : '00'}`);
  };
  return (
    <Paper radius="lg" shadow="xs" p="sm">
      <ScrollArea type="auto">
        <Box miw={760}>
          <Group gap={0} wrap="nowrap" align="stretch" pl={52} pb={6}>
            {dnevi.map((d) => (
              <Box key={d} style={{ flex: 1, minWidth: 0 }} ta="center">
                <Text size="sm" fw={700} c={d === danes ? barve.primarna : undefined}>
                  {imeDne(d, true)}
                </Text>
                {d === danes && (
                  <Badge size="xs" variant="light">
                    danes
                  </Badge>
                )}
              </Box>
            ))}
          </Group>
          <Group gap={0} wrap="nowrap" align="flex-start">
            <Box w={52} style={{ flexShrink: 0 }}>
              {ure.map((h) => (
                <Text
                  key={h}
                  size="xs"
                  c="dimmed"
                  h={URA_PX}
                  pr={8}
                  ta="right"
                  style={{ transform: 'translateY(-7px)' }}
                >
                  {h}.00
                </Text>
              ))}
            </Box>
            {dnevi.map((d) => (
              <Box
                key={d}
                data-dan={d}
                onClick={(e) => klik(d, e)}
                style={{
                  flex: 1,
                  minWidth: 0,
                  position: 'relative',
                  height: visina,
                  cursor: 'pointer',
                  borderLeft: `1px solid ${barve.obroba}`,
                  background:
                    `repeating-linear-gradient(to bottom, ${barve.obroba} 0 1px, transparent 1px ${URA_PX}px)` +
                    (d === danes ? `, ${barve.izbrano}` : ''),
                }}
              >
                {razporedi(poDnevih.get(d) ?? []).map(({ t, stolpec, stolpcev }) => {
                  const od = Math.max(PRVA_URA, Math.min(ZADNJA_URA - 0.5, urVDnevu(t.zacetek ?? '')));
                  const vrh = (od - PRVA_URA) * URA_PX;
                  const vis = Math.max(26, Math.min(visina - vrh, ((t.trajanje ?? 60) / 60) * URA_PX) - 2);
                  return (
                    <Blok
                      key={t.id}
                      t={t}
                      odpri={odpri}
                      style={{
                        position: 'absolute',
                        top: vrh + 1,
                        height: vis,
                        left: `calc(${(100 / stolpcev) * stolpec}% + 2px)`,
                        width: `calc(${100 / stolpcev}% - 4px)`,
                      }}
                    />
                  );
                })}
              </Box>
            ))}
          </Group>
        </Box>
      </ScrollArea>
    </Paper>
  );
}

/** Termin v mreži: čas, vozilo, stranka; sprejet je zelen s kljukico, potrjen ima ikono pisma. */
function Blok({ t, odpri, style }: { t: Termin; odpri: (t: Termin) => void; style: React.CSSProperties }) {
  const sprejet = !!t.nalogId;
  return (
    <UnstyledButton
      data-termin={t.id}
      onClick={(e) => {
        e.stopPropagation();
        odpri(t);
      }}
      aria-label={`Termin ${ura(t.zacetek)} ${t.kupec ?? ''}`}
      style={{
        display: 'block',
        textAlign: 'left',
        ...style,
        overflow: 'hidden',
        borderRadius: 8,
        padding: '4px 8px',
        background: sprejet ? 'var(--mantine-color-green-0)' : 'var(--mantine-color-hera-0)',
        borderLeft: `4px solid ${sprejet ? 'var(--mantine-color-green-6)' : barve.primarna}`,
        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.08)',
      }}
    >
      <Group gap={4} wrap="nowrap">
        <Text size="xs" fw={700} c={sprejet ? 'green.8' : barve.primarna} className="brez-preloma">
          {ura(t.zacetek)}–{ura(t.konec)}
        </Text>
        {sprejet && <IconCheck size={14} color="var(--mantine-color-green-7)" />}
        {!sprejet && t.potrjeno && (
          <IconMail size={14} color={barve.besediloSekundarno} aria-label="Potrditev poslana" />
        )}
      </Group>
      <Text size="sm" fw={600} lh={1.2} truncate>
        {t.vozilo?.registrska || [t.vozilo?.znamka, t.vozilo?.model].filter(Boolean).join(' ') || t.kupec}
      </Text>
      <Text size="xs" c="dimmed" lh={1.25} truncate>
        {[t.vozilo ? t.kupec : null, t.opis].filter(Boolean).join(' · ')}
      </Text>
    </UnstyledButton>
  );
}

/** Seznam po dnevih (telefon). */
function Seznam({ dnevi, danes, poDnevih, odpri, novNa }: Pogled) {
  return (
    <Stack gap="md">
      {dnevi.map((d) => (
        <div key={d}>
          <Group justify="space-between" mb={6}>
            <Text fw={700} c={d === danes ? barve.primarna : undefined}>
              {imeDne(d)}
              {d === danes ? ' · danes' : ''}
            </Text>
            <ActionIcon variant="subtle" size={36} aria-label={`Nov termin ${imeDne(d)}`} onClick={() => novNa(d)}>
              <IconPlus size={18} />
            </ActionIcon>
          </Group>
          {(poDnevih.get(d) ?? []).length === 0 ? (
            <Text size="sm" c="dimmed">
              Ni terminov.
            </Text>
          ) : (
            <Stack gap={6}>
              {(poDnevih.get(d) ?? []).map((t) => (
                <Blok key={t.id} t={t} odpri={odpri} style={{ width: '100%', padding: '8px 12px' }} />
              ))}
            </Stack>
          )}
        </div>
      ))}
    </Stack>
  );
}
